import React, { useEffect, useState } from "react";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import { Col, Row } from "react-bootstrap";
import Button from "../../../components/shared/buttons/button";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import { useUpdateShiftRecordMutation } from "./processingMenuApi";
import useTranslator from "../../../hooks/useTranslator";
import moment from "moment-timezone";
import { getAppTz, getCurrentDate } from "../../../helpers/Utils";

const parseShiftUtcDateTime = (value, fallbackDate = null) => {
  if (!value) {
    return null;
  }

  const input = String(value).trim();
  const utcDateTime = moment.utc(input, moment.ISO_8601, true);
  if (utcDateTime.isValid()) {
    return utcDateTime;
  }

  if (fallbackDate) {
    const combinedUtcDateTime = moment.utc(
      `${fallbackDate} ${input}`,
      ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD HH:mm:ss.SSS"],
      true
    );

    if (combinedUtcDateTime.isValid()) {
      return combinedUtcDateTime;
    }
  }

  const timeOnlyUtcDateTime = moment.utc(input, ["HH:mm:ss", "HH:mm"], true);
  return timeOnlyUtcDateTime.isValid() ? timeOnlyUtcDateTime : null;
};


const TimeModal = ({ show, mode, setShow, record, date }) => {
  // console.log(record)
  const { _t } = useTranslator();
  const initialFormData = {
    end_time: "",
    start_time: "",
    name: mode === "morning" ? _t("morning_shift", "Morning Shift") : _t("evening_shift", "Evening Shift"),
  };

  const [formData, setFormData] = useState(initialFormData);
  const [updateRecord] = useUpdateShiftRecordMutation();

  const convertUtcDateTimeToUserDateTime = (utcValue) => {
    if (!utcValue) return "";

    const timezone = getAppTz();
    const baseDate = record?.date && moment.utc(record.date).isValid()
      ? moment.utc(record.date).format("YYYY-MM-DD")
      : (date || getCurrentDate("YYYY-MM-DD"));
    const utcMoment = parseShiftUtcDateTime(utcValue, baseDate);

    if (!utcMoment || !utcMoment.isValid()) {
      return "";
    }

    return utcMoment.tz(timezone).format("YYYY-MM-DDTHH:mm");
  };

  const convertUserDateTimeToUtc = (userDateTime) => {
    if (!userDateTime) return "";

    const timezone = getAppTz();
    const userMoment = moment.tz(userDateTime, "YYYY-MM-DDTHH:mm", true, timezone);

    if (!userMoment.isValid()) {
      return "";
    }

    return userMoment.utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
  };

  useEffect(() => {
    if (record) {
      const updatedFormData = { ...initialFormData };
      for (const key in record) {
        if (key in initialFormData) {
          if (key === "start_time" || key === "end_time") {
            updatedFormData[key] = convertUtcDateTimeToUserDateTime(record[key]);
          } else {
            updatedFormData[key] = record[key];
          }
        }
      }
      setFormData(updatedFormData);
    } else {
      setFormData(initialFormData);
    }
  }, [mode, record, show, date]);

  const handleClose = () => {
    setShow(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formattedData = {
      ...formData,
      start_time: convertUserDateTimeToUtc(formData.start_time),
      end_time: convertUserDateTimeToUtc(formData.end_time),
    };

    const startMoment = moment.utc(formattedData.start_time, moment.ISO_8601, true);
    const endMoment = moment.utc(formattedData.end_time, moment.ISO_8601, true);

    if (startMoment.isValid() && endMoment.isValid() && endMoment.isSameOrBefore(startMoment)) {
      formattedData.end_time = endMoment.add(1, "day").format("YYYY-MM-DDTHH:mm:ss[Z]");
    }

    try {
      const payload = {
        id: record.id,
        requestBody: formattedData,
      };

      await updateRecord(payload).unwrap();
      handleClose();
    } catch (error) {
      console.error("Error creating/updating manager:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const renderFormFields = () => (
    <>
      <Col lg={12}>
        <InputWithLabel
          label={_t("start_time", "Start Date & Time")}
          type="datetime-local"
          name="start_time"
          value={formData.start_time}
          onChange={handleChange}
        />
      </Col>
      <Col lg={12}>
        <InputWithLabel
          label={_t("end_time", "End Date & Time")}
          type="datetime-local"
          name="end_time"
          value={formData.end_time}
          onChange={handleChange}
        />
      </Col>
    </>
  );

  return (
    <CommonModal
      show={show}
      className="time_modal"
      handleClose={handleClose}
      setShow={setShow}
      title={mode === "morning" ? _t("morning_shift", "Morning Shift") : _t("evening_shift", "Evening Shift")}
    >
      <Row>{renderFormFields()}</Row>
      <Row>
        <Col className="text-end">
          <Button label={_t("save_changes", "Save Changes")} size="small" onClick={handleSubmit} />
        </Col>
      </Row>
    </CommonModal>
  );
};

export default TimeModal;
